// ---------------------------------------------------------------------------
// The visit sheet. Assume one thumb, direct sunlight, standing on a porch
// (PLAN 8, docs/campaign-requirements.md § 3.1).
//
//   * `not_home` is one tap and saves immediately -- it is the most common
//     outcome by a wide margin, and everything else is subordinate to it.
//   * The household is shown the moment the sheet opens, before any button is
//     pressed: a canvasser walking up needs to know who lives here, so they can
//     ask for someone by name. Choosing "answered" turns that same list from
//     context into controls, each person marked independently of the others.
//   * Notes are never required.
//   * Nothing here awaits the network: recordVisit() returns synchronously.
//
// The member list shows names, party, age and gender. That is voter
// registration information on a canvasser's phone; see
// docs/campaign-requirements.md § 5 for what that entails.
// ---------------------------------------------------------------------------

import { useLayoutEffect, useRef, useState } from 'react';
import {
  BEST_RETURN_TIMES,
  INACCESSIBLE_LABELS,
  INACCESSIBLE_REASONS,
  LITERATURE,
  LITERATURE_LABELS,
  MAX_NOTE_LENGTH,
  OUTCOME_LABELS,
  PARTY_LABELS,
  RETURN_TIME_LABELS,
  SUPPORT_LABELS,
  SUPPORT_LEVELS,
  canUndoDoNotContact,
  formatAddress,
  formatName,
  type BestReturnTime,
  type HouseholdWithStatus,
  type InaccessibleReason,
  type Literature,
  type PersonWithContact,
  type SupportLevel,
  type VisitOutcome,
} from '@yucanvas/shared';
import type { ReviewVisit } from '../data/turf-review';
import {
  flagDoNotContact,
  unflagDoNotContact,
  newContactId,
  newVisitId,
  recordHouseNote,
  recordNewHousehold,
  recordNewPerson,
  recordPersonContact,
  recordVisit,
  removeHousehold,
  findAddressClash,
  reviveHousehold,
} from '../data/writes';
import type { LocalHouseNote } from '../offline/db';
import { relativeTime } from '../lib/time';
import { useAuth } from '../auth/AuthProvider';

/** Behind the three primary buttons: rare, and about the address, not the door. */
const ADDRESS_PROBLEMS: VisitOutcome[] = ['vacant', 'wrong_address'];

/** What a canvasser recorded for one person before hitting save. */
interface PersonEntry {
  spoke: boolean;
  support: SupportLevel | null;
  signRequested: boolean;
}

const BLANK: PersonEntry = { spoke: false, support: null, signRequested: false };

interface Props {
  /** An existing home. Omitted when adding one. */
  household?: HouseholdWithStatus;
  /** Where the canvasser tapped, when adding a home that is not on the list. */
  newHomeAt?: { lat: number; lon: number };
  turfId?: string;
  /** Street of the nearest known home, to prefill the address. */
  nearbyAddress?: string | null;
  people: PersonWithContact[];
  notes: LocalHouseNote[];
  /**
   * Every knock at this address, newest first. Only drawn when `readOnly`:
   * a canvasser on a porch needs the next action, not the archive, and their
   * device has no roster to attribute the rows with anyway.
   */
  visits?: ReviewVisit[];
  /**
   * Somebody looking at turf that is not theirs. Every control that records
   * something is withdrawn, because the server would refuse the write --
   * organizers hold the reads on another turf and not the inserts
   * (20260915000100_rls_evaluate_once.sql) -- and because a queued write has
   * no device store here to live in.
   */
  readOnly?: boolean;
  canvasserId: string;
  canvasserName: string;
  onClose: () => void;
}

export function VisitSheet({
  household,
  newHomeAt,
  turfId,
  nearbyAddress,
  people,
  notes,
  visits = [],
  readOnly = false,
  canvasserId,
  canvasserName,
  onClose,
}: Props) {
  const adding = household === undefined;

  // Only used while adding: the address the canvasser is standing at, and who
  // answered. Prefilled with the nearest street so they type a house number
  // rather than a street they can read off the sign.
  const [street, setStreet] = useState(nearbyAddress ? ` ${nearbyAddress}` : '');
  const [unit, setUnit] = useState('');
  const [residentName, setResidentName] = useState('');
  const [newHomeSupport, setNewHomeSupport] = useState<SupportLevel | null>(null);
  const [newHomeSign, setNewHomeSign] = useState(false);
  const [outcome, setOutcome] = useState<VisitOutcome | null>(null);
  const [reason, setReason] = useState<InaccessibleReason | null>(null);
  const [literature, setLiterature] = useState<Literature>('none');
  const [returnTime, setReturnTime] = useState<BestReturnTime | null>(null);
  const [entries, setEntries] = useState<Record<string, PersonEntry>>({});
  const [visitNotes, setVisitNotes] = useState('');
  const [houseNote, setHouseNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [flagging, setFlagging] = useState(false);
  const [unflagging, setUnflagging] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Undoing a flag is the one thing here that depends on who is looking: a
  // canvasser may take back their own recent tap, an organizer may clear any
  // flag at all (canUndoDoNotContact, PLAN 11).
  const { profile } = useAuth();

  // Opened at the top, always. Belt to the sticky header's braces: a sheet
  // that opens mid-content hides the address a canvasser is standing in front
  // of.
  const sheetRef = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    sheetRef.current?.scrollTo({ top: 0 });
  }, []);

  const entryFor = (id: string): PersonEntry => entries[id] ?? BLANK;

  function update(id: string, patch: Partial<PersonEntry>): void {
    setEntries((prev) => ({ ...prev, [id]: { ...entryFor(id), ...patch } }));
  }

  async function save(finalOutcome: VisitOutcome): Promise<void> {
    try {
      const visitId = newVisitId();
      const now = new Date().toISOString();

      // Adding a home creates it first, so the visit has something to point
      // at. Both are queued locally, so this works with no signal -- which is
      // the situation a canvasser is in when they find a house nobody knew
      // about.
      let targetId = household?.id;
      if (adding) {
        if (!turfId || !newHomeAt) throw new Error('No turf for this home.');

        // Checked here rather than left to the database. The address is
        // unique, and a removed home still holds its slot -- so without this
        // the canvasser saves, walks on, and finds out at the next sync that
        // nothing was recorded.
        const clash = await findAddressClash(turfId, street, unit || null, null);

        if (clash?.kind === 'active') {
          throw new Error(
            'That address is already on the list. Close this and tap its pin instead.',
          );
        }

        if (clash?.kind === 'removed') {
          // The same address, brought back rather than duplicated: the knocks
          // already logged there belong to this home.
          await reviveHousehold(clash.household.id, newHomeAt);
          targetId = clash.household.id;
        } else {
          const created = recordNewHousehold({
            turfId,
            lat: newHomeAt.lat,
            lon: newHomeAt.lon,
            streetAddress: street,
            unit,
            city: 'Yucaipa',
            postalCode: null,
          });
          targetId = created.id;
        }

        if (finalOutcome === 'answered' && residentName.trim() !== '' && targetId) {
          const person = recordNewPerson(targetId, residentName);
          recordPersonContact({
            id: newContactId(),
            visit_id: visitId,
            person_id: person.id,
            canvasser_id: canvasserId,
            contacted_at: now,
            spoke: true,
            support: newHomeSupport,
            sign_requested: newHomeSign,
            notes: null,
          });
        }
      }
      if (!targetId) throw new Error('No home to record against.');

      recordVisit({
        id: visitId,
        household_id: targetId,
        canvasser_id: canvasserId,
        visited_at: now,
        outcome: finalOutcome,
        inaccessible_reason: finalOutcome === 'inaccessible' ? reason : null,
        literature,
        best_return_time: finalOutcome === 'not_home' ? returnTime : null,
        sign_requested: false,
        volunteer_interest: false,
        notes: visitNotes,
      });

      // Only people the canvasser actually touched get a row. A silent
      // no-answer for everyone else would be a claim nobody made.
      if (finalOutcome === 'answered' && !adding) {
        for (const person of people) {
          const entry = entryFor(person.id);
          if (!entry.spoke && entry.support === null && !entry.signRequested) continue;

          recordPersonContact({
            id: newContactId(),
            visit_id: visitId,
            person_id: person.id,
            canvasser_id: canvasserId,
            contacted_at: now,
            spoke: entry.spoke,
            support: entry.spoke ? entry.support : null,
            sign_requested: entry.signRequested,
            notes: null,
          });
        }
      }

      onClose();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'That visit could not be saved.',
      );
    }
  }

  async function flag(): Promise<void> {
    setFlagging(true);
    try {
      const why = visitNotes.trim() === '' ? null : visitNotes.trim();
      // Updates the device store, so the screens re-render without a callback.
      if (!household) return;
      await flagDoNotContact(household.id, why, canvasserId);
      onClose();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Could not flag this address.',
      );
    } finally {
      setFlagging(false);
    }
  }

  /** Their own flag, as opposed to an organizer clearing somebody else's. */
  const ownFlag =
    household !== undefined &&
    profile !== null &&
    household.do_not_contact_by === profile.id;

  // Not offered while reviewing. Clearing a flag mirrors itself into
  // db.households straight afterwards so the address reappears at once, and on
  // turf this device was never assigned there is no row there to mirror into --
  // the RPC would succeed and the screen would go on showing the flag. The
  // reports screen is where a flag on somebody else's turf gets cleared.
  const canUndo =
    !readOnly &&
    household !== undefined &&
    profile !== null &&
    canUndoDoNotContact(household, { id: profile.id, role: profile.role });

  async function unflag(): Promise<void> {
    if (!household) return;

    // Setting the flag is one tap with no confirm, so taking your own back is
    // one tap too -- a confirm on the undo of a mis-tap is just a second tap to
    // regret. Clearing somebody else's is an organizer overriding a recorded
    // request, and gets the same confirm the reports screen asks for.
    if (
      !ownFlag &&
      !window.confirm(
        `Put ${formatAddress(household)} back in the walk list? Canvassers ` +
          `will be asked to knock it again.`,
      )
    ) {
      return;
    }

    setUnflagging(true);
    try {
      await unflagDoNotContact(household.id);
      // Deliberately does not close: the flag was what stopped this door being
      // worked, and the canvasser is standing at it. The banner disappearing
      // is the confirmation, and the visit buttons are right there.
      setError(null);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Could not undo this flag.',
      );
    } finally {
      setUnflagging(false);
    }
  }

  const remaining = MAX_NOTE_LENGTH - visitNotes.length;
  // Deceased and moved are shown rather than hidden: not asking for someone
  // who has died is precisely the context a canvasser needs at the door. They
  // are muted and cannot be marked.
  const listed = people.filter((p) => p.active);
  // A home with no address is not a home: the column is NOT NULL, and a walk
  // list of unnamed pins helps nobody.
  const ready = !adding || street.trim() !== '';

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <section
        ref={sheetRef}
        className="sheet"
        role="dialog"
        aria-label={
          household ? `Log a visit at ${formatAddress(household)}` : 'Add a home'
        }
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sheet__head">
          <div>
            <h2>{household ? formatAddress(household) : 'Add a home'}</h2>
            <p className="sheet__sub">
              {household ? (
                <>
                  {listed.length === 1 ? '1 voter' : `${listed.length} voters`}
                  {household.visit_count > 0
                    ? ` · ${household.visit_count} previous ${
                        household.visit_count === 1 ? 'visit' : 'visits'
                      }`
                    : ''}
                  {household.do_not_contact ? ' · Do not contact' : ''}
                </>
              ) : (
                'Not on the walk list'
              )}
            </p>
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>

        {household?.do_not_contact ? (
          <div className="banner banner--warn banner--dnc">
            <p>
              This address is flagged do not contact. Do not knock.
              {/* Somebody who cannot undo it should still be told that it can
                  be undone, and by whom. Not saying so is what made the flag
                  feel permanent. An organizer reading somebody else's turf is
                  already the person who can, and is pointed at where. */}
              {canUndo ? '' : readOnly ? ' Clear it from Reports.' : ' An organizer can clear it.'}
            </p>
            {canUndo ? (
              <button
                className="btn banner__undo"
                disabled={unflagging}
                onClick={() => void unflag()}
              >
                {unflagging
                  ? 'Undoing\u2026'
                  : ownFlag
                    ? 'Undo \u2014 I flagged this by mistake'
                    : 'Clear this flag'}
              </button>
            ) : null}
          </div>
        ) : null}

        {error ? <p className="banner banner--error">{error}</p> : null}

        {household ? (
          <HouseNotes
          notes={notes}
          readOnly={readOnly}
          draft={houseNote}
          onDraftChange={setHouseNote}
          onAdd={() => {
            const body = houseNote.trim();
            if (body === '') return;
              recordHouseNote(
                {
                  id: newContactId(),
                  household_id: household.id,
                author_id: canvasserId,
                created_at: new Date().toISOString(),
                body,
              },
              canvasserName,
            );
              setHouseNote('');
            }}
          />
        ) : null}

        {adding ? (
          <div className="sheet__body">
            <p className="label">Address</p>
            <label className="field">
              <span>Street address</span>
              <input
                value={street}
                placeholder="e.g. 1247 Oak Glen Rd"
                autoFocus
                onChange={(e) => setStreet(e.target.value)}
              />
            </label>
            <label className="field">
              <span>
                Unit <span className="label__hint">optional</span>
              </span>
              <input
                value={unit}
                placeholder="e.g. B"
                onChange={(e) => setUnit(e.target.value)}
              />
            </label>
            <p className="muted small">
              Dropped at {newHomeAt?.lat.toFixed(5)}, {newHomeAt?.lon.toFixed(5)}.
              Nobody on the voter file lives here, so anyone you speak to is
              recorded by name.
            </p>
          </div>
        ) : null}

        {/* Context before anything is pressed. Once the outcome is "answered"
            these same rows become the controls. */}
        {!adding ? (
        <div className="sheet__body">
          <p className="label">
            Who lives here
            {outcome === 'answered' ? (
              <span className="label__hint"> · tap each person you spoke to</span>
            ) : null}
          </p>

          {listed.length === 0 ? (
            <p className="muted small">No registered voters on file at this address.</p>
          ) : null}

          <ul className="people">
            {listed.map((person) => (
              <PersonRow
                key={person.id}
                person={person}
                entry={entryFor(person.id)}
                interactive={outcome === 'answered' && !person.deceased && !person.moved}
                onChange={(patch) => update(person.id, patch)}
              />
            ))}
          </ul>
        </div>
        ) : null}

        {readOnly ? (
          <VisitHistory visits={visits} />
        ) : outcome === null ? (
          <div className="sheet__body">
            {/* One tap, from pin to saved. */}
            {adding && !ready ? (
              <p className="muted small">Enter the address to continue.</p>
            ) : null}

            <button
              className="btn btn--primary btn--huge"
              disabled={!ready}
              onClick={() => void save('not_home')}
            >
              Not home
            </button>
            <button
              className="btn btn--ghost"
              disabled={!ready}
              onClick={() => setOutcome('not_home')}
            >
              Not home — add a return time
            </button>

            <button
              className="btn btn--secondary btn--huge"
              disabled={!ready}
              onClick={() => setOutcome('answered')}
            >
              Answered
            </button>
            <button
              className="btn btn--secondary btn--huge"
              disabled={!ready}
              onClick={() => setOutcome('inaccessible')}
            >
              Inaccessible
            </button>

            <p className="label">Something wrong with this address?</p>
            <div className="grid">
              {ADDRESS_PROBLEMS.map((o) => (
                <button key={o} className="btn btn--tile" onClick={() => setOutcome(o)}>
                  {OUTCOME_LABELS[o]}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="sheet__body">
            <div className="chosen">
              <span>{OUTCOME_LABELS[outcome]}</span>
              <button
                className="btn btn--link"
                onClick={() => {
                  setOutcome(null);
                  setReason(null);
                  setReturnTime(null);
                }}
              >
                Change
              </button>
            </div>

            {outcome === 'not_home' ? (
              <>
                <p className="label">Best time to come back</p>
                <div className="chips">
                  {BEST_RETURN_TIMES.map((t) => (
                    <button
                      key={t}
                      className={`chip${returnTime === t ? ' chip--on' : ''}`}
                      onClick={() => setReturnTime(returnTime === t ? null : t)}
                    >
                      {RETURN_TIME_LABELS[t]}
                    </button>
                  ))}
                </div>
              </>
            ) : null}

            {outcome === 'inaccessible' ? (
              <>
                <p className="label">What stopped you</p>
                <div className="chips">
                  {INACCESSIBLE_REASONS.map((r) => (
                    <button
                      key={r}
                      className={`chip${reason === r ? ' chip--on' : ''}`}
                      onClick={() => setReason(reason === r ? null : r)}
                    >
                      {INACCESSIBLE_LABELS[r]}
                    </button>
                  ))}
                </div>
              </>
            ) : null}

            {/* A home nobody had is a home nobody is on file at, so the
                person who answered is recorded by name rather than picked
                from a list. */}
            {adding && outcome === 'answered' ? (
              <>
                <p className="label">Who did you speak to?</p>
                <label className="field">
                  <span>
                    Name <span className="label__hint">optional</span>
                  </span>
                  <input
                    value={residentName}
                    placeholder="e.g. Maria Delgado"
                    onChange={(e) => setResidentName(e.target.value)}
                  />
                </label>

                {residentName.trim() !== '' ? (
                  <>
                    <div className="chips">
                      {SUPPORT_LEVELS.map((level) => (
                        <button
                          key={level}
                          className={`chip${newHomeSupport === level ? ' chip--on' : ''}`}
                          onClick={() =>
                            setNewHomeSupport(newHomeSupport === level ? null : level)
                          }
                        >
                          {SUPPORT_LABELS[level]}
                        </button>
                      ))}
                    </div>
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={newHomeSign}
                        onChange={(e) => setNewHomeSign(e.target.checked)}
                      />
                      <span>Wants a yard sign</span>
                    </label>
                  </>
                ) : null}
              </>
            ) : null}

            <p className="label">Literature</p>
            <div className="chips">
              {LITERATURE.map((l) => (
                <button
                  key={l}
                  className={`chip${literature === l ? ' chip--on' : ''}`}
                  onClick={() => setLiterature(l)}
                >
                  {LITERATURE_LABELS[l]}
                </button>
              ))}
            </div>

            <p className="label">
              Notes <span className="label__hint">optional</span>
            </p>
            <textarea
              className="notes"
              value={visitNotes}
              maxLength={MAX_NOTE_LENGTH}
              rows={3}
              placeholder="Anything the next canvasser needs to know."
              onChange={(e) => setVisitNotes(e.target.value)}
            />
            <p className={`counter${remaining < 40 ? ' counter--low' : ''}`}>
              {remaining} characters left · permanent, visible to the whole campaign
            </p>

            <button
              className="btn btn--primary btn--huge"
              disabled={!ready}
              onClick={() => void save(outcome)}
            >
              {adding ? 'Add home and save' : 'Save visit'}
            </button>
          </div>
        )}

        {household && !readOnly ? (
          <footer className="sheet__foot">
            {!household.do_not_contact ? (
              <button className="btn btn--danger-link" onClick={flag} disabled={flagging}>
                {flagging ? 'Flagging…' : 'Flag do not contact'}
              </button>
            ) : null}

            {/* Only a home somebody added by hand. A voter-file address is on
                everyone's walk list and is not a canvasser's to withdraw. */}
            {household.source === 'manual' ? (
              <button
                className="btn btn--danger-link"
                disabled={removing}
                onClick={() => {
                  if (
                    !window.confirm(
                      `Remove ${formatAddress(household)}? It was added by a ` +
                        `canvasser. Anything already recorded here is kept.`,
                    )
                  ) {
                    return;
                  }
                  setRemoving(true);
                  removeHousehold(household.id)
                    .then(onClose)
                    .catch((cause: unknown) => {
                      setError(
                        cause instanceof Error
                          ? cause.message
                          : 'Could not remove this home.',
                      );
                      setRemoving(false);
                    });
                }}
              >
                {removing ? 'Removing…' : 'Remove this home'}
              </button>
            ) : null}
          </footer>
        ) : null}
      </section>
    </div>
  );
}

/**
 * Every knock at one address, for somebody reading rather than knocking.
 *
 * This is the part of the detail view an organizer cannot get anywhere else:
 * the reports screen aggregates a turf into counts, and the door-level export
 * is a CSV. Here it is the history of one address -- who went, when, and what
 * happened -- which is the question actually being asked when an organizer
 * clicks a turf and starts looking at houses.
 */
function VisitHistory({ visits }: { visits: ReviewVisit[] }) {
  return (
    <div className="sheet__body">
      <p className="label">
        Visit history
        {visits.length > 0 ? (
          <span className="label__hint"> · {visits.length}</span>
        ) : null}
      </p>

      {visits.length === 0 ? (
        <p className="muted small">Nobody has knocked here yet.</p>
      ) : (
        <ul className="visit-log">
          {visits.map((v) => (
            <li key={v.id} className="visit-log__row">
              <span className="visit-log__outcome">{OUTCOME_LABELS[v.outcome]}</span>
              <span className="visit-log__by">
                {v.canvasser_name ?? 'A canvasser'} · {relativeTime(v.visited_at)}
                {v.inaccessible_reason
                  ? ` · ${INACCESSIBLE_LABELS[v.inaccessible_reason]}`
                  : ''}
                {v.best_return_time
                  ? ` · back ${RETURN_TIME_LABELS[v.best_return_time]}`
                  : ''}
                {v.literature !== 'none' ? ` · ${LITERATURE_LABELS[v.literature]}` : ''}
              </span>
              {v.notes ? <p className="visit-log__note">{v.notes}</p> : null}
            </li>
          ))}
        </ul>
      )}

      <p className="muted small">
        Read-only: this turf is not assigned to you, so nothing here can be
        recorded from this screen.
      </p>
    </div>
  );
}

/**
 * One household member.
 *
 * Read-only until the door is answered, because its first job is context: who
 * is behind this door, how are they registered, and what did they say last
 * time. `interactive` flips the same row into the control a canvasser marks.
 */
function PersonRow({
  person,
  entry,
  interactive,
  onChange,
}: {
  person: PersonWithContact;
  entry: PersonEntry;
  interactive: boolean;
  onChange: (patch: Partial<PersonEntry>) => void;
}) {
  const gone = person.deceased || person.moved;

  const meta = (
    <span className="person__meta">
      {person.party ? (
        <span
          className={`party party--${person.party.toLowerCase()}`}
          title={PARTY_LABELS[person.party]}
        >
          {person.party}
        </span>
      ) : null}
      {person.age !== null ? <span>{person.age}</span> : null}
      {person.gender ? <span>{person.gender}</span> : null}
    </span>
  );

  // What happened last time, so nobody opens with a question already answered.
  const history = person.last_contact ? (
    <span className="person__history">
      {person.last_contact.support
        ? SUPPORT_LABELS[person.last_contact.support]
        : person.last_contact.spoke
          ? 'Spoken to'
          : 'Logged'}
      {' · '}
      {relativeTime(person.last_contact.contacted_at)}
      {person.last_contact.sign_requested ? ' · wants a sign' : ''}
    </span>
  ) : null;

  const context = (
    <span className="person__context">
      {person.deceased ? <span className="flag flag--warn">deceased</span> : null}
      {person.moved ? <span className="flag flag--warn">moved</span> : null}
      {person.do_not_call ? <span className="flag">no call</span> : null}
      {history}
    </span>
  );

  const inner = (
    <>
      <span className="person__lines">
        <span className="person__name">{formatName(person)}</span>
        {context}
      </span>
      {meta}
    </>
  );

  return (
    <li className={`person${gone ? ' person--gone' : ''}`}>
      {interactive ? (
        <button
          className={`person__head${entry.spoke ? ' person__head--on' : ''}`}
          onClick={() => onChange({ spoke: !entry.spoke })}
          aria-pressed={entry.spoke}
        >
          {inner}
        </button>
      ) : (
        // A plain div, not a disabled button: before the door is answered this
        // is information, and it should not look like something to press.
        <div className="person__head person__head--static">{inner}</div>
      )}

      {interactive && entry.spoke ? (
        <div className="person__body">
          <div className="chips">
            {SUPPORT_LEVELS.map((s) => (
              <button
                key={s}
                className={`chip${entry.support === s ? ' chip--on' : ''}`}
                onClick={() => onChange({ support: entry.support === s ? null : s })}
              >
                {SUPPORT_LABELS[s]}
              </button>
            ))}
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={entry.signRequested}
              onChange={(e) => onChange({ signRequested: e.target.checked })}
            />
            <span>Wants a yard sign</span>
          </label>
        </div>
      ) : null}
    </li>
  );
}

/**
 * The note thread for an address.
 *
 * Notes are rows, not a field: two canvassers writing about the same house at
 * once produce two notes, and neither erases the other
 * (docs/campaign-requirements.md § 3.6). Saving adds to the thread; there is
 * no editing, because a note is an observation somebody made.
 */
function HouseNotes({
  notes,
  readOnly,
  draft,
  onDraftChange,
  onAdd,
}: {
  notes: LocalHouseNote[];
  readOnly: boolean;
  draft: string;
  onDraftChange: (value: string) => void;
  onAdd: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="sheet__body">
      <p className="label">
        About this house
        {notes.length > 0 ? (
          <span className="label__hint"> · {notes.length}</span>
        ) : null}
      </p>

      {notes.length === 0 ? (
        <p className="muted small">Nothing noted yet.</p>
      ) : (
        <ul className="notes-thread">
          {notes.map((note) => (
            <li key={note.id} className="note">
              <p className="note__body">{note.body}</p>
              <p className="note__by">
                {note.author_name ?? 'A canvasser'} · {relativeTime(note.created_at)}
                {note.synced === 0 ? ' · not uploaded yet' : ''}
              </p>
            </li>
          ))}
        </ul>
      )}

      {readOnly ? null : open ? (
        <>
          <textarea
            className="notes"
            value={draft}
            maxLength={MAX_NOTE_LENGTH}
            rows={2}
            placeholder="Anything the next canvasser should know about the house."
            onChange={(e) => onDraftChange(e.target.value)}
          />
          <div className="row-actions">
            <button
              className="btn btn--secondary"
              disabled={draft.trim() === ''}
              onClick={() => {
                onAdd();
                setOpen(false);
              }}
            >
              Add note
            </button>
            <button className="btn btn--link" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        <button className="btn btn--ghost" onClick={() => setOpen(true)}>
          Add a note
        </button>
      )}
    </div>
  );
}
